import { useFormatVariableValue } from "@/hooks";
import { useGetAllElementsAsync } from "@/hooks/useGetElementAsync";
import { sapienAxios } from "@/inertia-utils/hooks";
import { ModelVariableScope, ModelVariableUnit } from "@/models";
import { LaravelRequestBodyShapes } from "@/ziggy-shims";
import { useQuery } from "@tanstack/react-query";

export async function getModelValue(richTextModelQuantityIds: string[]) {
    const { data } = await sapienAxios.post<{
        values: {
            numerical_value: number;
            is_integer: boolean;
            unit: ModelVariableUnit;
            time_horizon_id: string;
            model_variable_id: string;
            scope: ModelVariableScope;
            uses_time: boolean;
            label: string;
            time_index: number;
        }[];
        richTextModelQuantities: {
            id: string;
            model_variable_id: string;
            time_horizon_id: string;
        }[];
    }>("get-model-value", {
        rich_text_model_quantity_ids: richTextModelQuantityIds,
    } as unknown as (typeof LaravelRequestBodyShapes)["get-model-value"]);

    return data;
}

export function useRichTextModelQuantities() {
    const formatVariableValue = useFormatVariableValue();

    const childElements = useGetAllElementsAsync(
        "[data-rich-text-model-quantity-id]",
    );
    const richTextModelQuantityIds = [];

    const ids = new Set<string>();
    if (!!childElements) {
        for (const childElement of Array.from(childElements)) {
            const richTextModelQuantityId = childElement.getAttribute(
                "data-rich-text-model-quantity-id",
            );
            richTextModelQuantityIds.push(richTextModelQuantityId);
            ids.add(richTextModelQuantityId);
        }
    }

    const { data: modelValues, isLoading } = useQuery({
        queryKey: ["model-value", childElements?.length, JSON.stringify(ids)],
        queryFn: () => getModelValue(richTextModelQuantityIds),
        enabled: !!richTextModelQuantityIds?.length,
        select: (
            data,
        ): Record<
            string,
            {
                value: string;
                numericalValue: number;
                modelVariableId: string;
                timeHorizonId: string;
                label: string;
                timeIndex: number;
            }
        > => {
            return data.richTextModelQuantities.reduce((acc, quantity) => {
                const value = data.values.find(
                    (v) =>
                        v.model_variable_id === quantity.model_variable_id &&
                        v.time_horizon_id === quantity.time_horizon_id,
                );

                const key = quantity.id;

                return {
                    ...acc,
                    [key]: {
                        value: formatVariableValue(
                            value.unit,
                            value.numerical_value,
                            value.is_integer,
                        ),
                        numericalValue: value.numerical_value,
                        modelVariableId: quantity.model_variable_id,
                        timeHorizonId: quantity.time_horizon_id,
                        label: value.label,
                        timeIndex: value.time_index,
                    },
                };
            }, {});
        },
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    });

    return { modelValues, isLoading, elements: childElements };
}
